// Global Instructions Rule Applied!
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { Row, Col } from 'antd'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes } from '@fortawesome/free-solid-svg-icons'
import { TasksSection } from '../../../../tasks/components/index.ts'
import { InteractionsSection } from '../../../../interactions/components/index.ts'
import { DetailPanel } from '../parts/DetailPanel.tsx'
import { DealTeam } from '../parts/DealTeam.tsx'
import { TeamMemberDrawer } from '../parts/TeamMemberDrawer.tsx'
import { DealOverviewSection } from '../sections/DealOverviewSection.tsx'
import { CompanyTeamTable } from '../../../../../core/components/CompanyTeamTable/index.tsx'
import { AgentSignalsSection } from '../../../../internal_agents/components/AgentSignalsSection.tsx'
import { DealStatesStrip } from '../../../../internal_agents/components/DealStatesStrip.tsx'
import { DealBriefCard } from '../../../../internal_agents/components/DealBriefCard.tsx'

import { useSelector } from 'react-redux'
import { useAppDispatch } from '../../../../../state_manager/hooks/useAppDispatch'
import { createTask, updateTask } from '../../../../tasks/services/tasksService.ts'
import { deleteTask as deleteTaskThunk } from '../../../../../store/slices/tasksSlice'
import {
  createInteraction,
  updateInteraction,
  deleteInteraction,
  fetchInteractionsForDeal,
  delinkInteraction
} from '../../../../interactions/services/interactionsService.ts'
import { updateDealRecord } from '../../../../../state_manager/state_update_actions/deals/deal_actions.ts'
import { selectDealById } from '../../../../../state_manager/state_update_actions/deals/selectors.ts'
import {
  addInteraction,
  updateInteraction as updateInteractionAction,
  setInteractions,
  removeInteraction
} from '../../../../../state_manager/state_update_actions/interactions/interaction_actions.ts'
import { DealFields } from '../../../../../state_manager/data_models/deal_record.ts'
import { UserEntryFields } from '../../../../../state_manager/data_models/user_entry.ts'
import { UNKNOWN_CONTACT } from '../../../../../state_manager/constants/strings.ts'
import { TaskRecordFields } from '../../../../../state_manager/data_models/task_record.ts'
import { InteractionRecordFields } from '../../../../../state_manager/data_models/interaction_record.ts'
import { fetchDealById } from '../../../services/dealsService.ts'
import { getAccessToken } from '../../../../../core/auth/authService'
import { store } from '../../../../../state_manager/store.ts'
import {
  setActiveRecord,
  updateActiveRecord,
  registerRecordUsage
} from '../../../../../state_manager/state_update_actions/active_records/active_record_actions.ts'

interface DashboardTabProps {
  dealTeam?: any[]
  dealRecord?: any
  interactions?: any[]
  handleFieldChange?: (key: string, value: any) => void
  onPatchSuccess?: (patchRecordId: string, updates: Record<string, any>) => void
  recordId?: string | null
  commitments?: any[]
  handleCommitmentChange?: (commitments: any[]) => void
  getCurrencySymbol?: (currencyId: string) => string
  companyTeamMembers?: any[]
  companyTeamMembersKey?: string
  onCompanyTeamMembersChange?: (members: any[]) => void
  onRefreshInteractionsReady?: (refreshFn: () => Promise<void>) => void
  onRefreshTasksReady?: (refreshFn: () => Promise<void>) => void
  /** Deal-follower panels (rendered only while a follower exists). */
  onRefreshAgentReady?: (refreshFn: () => Promise<void>) => void
  onRefreshDealStatesReady?: (refreshFn: () => Promise<void>) => void
  onNavigateToAgentTab?: () => void
  showAgentPanels?: boolean
}

export function DashboardTab({
  dealRecord,
  handleFieldChange,
  onPatchSuccess,
  recordId,
  commitments = [],
  handleCommitmentChange,
  companyTeamMembers: propCompanyTeamMembers,
  companyTeamMembersKey,
  onCompanyTeamMembersChange,
  onRefreshInteractionsReady,
  onRefreshTasksReady,
  onRefreshAgentReady,
  onRefreshDealStatesReady,
  onNavigateToAgentTab,
  showAgentPanels = false
}: DashboardTabProps) {
  const dispatch = useAppDispatch()
  const currentDeal = useSelector((state: any) =>
    recordId && recordId !== 'new' ? selectDealById(state, recordId) : null
  )
  const [selectedTeamMember, setSelectedTeamMember] = useState<any>(null)
  const [isTaskInEditMode, setIsTaskInEditMode] = useState<boolean>(false)

  const [selectedContacts, setSelectedContacts] = useState<{
    introduced_by: any[]
    sourced_by: any[]
    deal_team_lead: any[]
  }>({
    introduced_by: [],
    sourced_by: [],
    deal_team_lead: []
  })

  const companyTeamMembers = propCompanyTeamMembers || []

  useEffect(() => {
    if (dealRecord) {
      const contactsByRole: {
        introduced_by: any[]
        sourced_by: any[]
        deal_team_lead: any[]
      } = {
        introduced_by: [],
        sourced_by: [],
        deal_team_lead: []
      }

      const introducedBy = dealRecord[DealFields.contact_accounts__introduced_by] || dealRecord.introduced_by_detail
      if (introducedBy && Array.isArray(introducedBy)) {
        contactsByRole.introduced_by = introducedBy.map((contact: any) => ({
          value: contact[UserEntryFields.contacts__id] || '',
          label:
            contact[UserEntryFields.prog__display_name] ||
            `${contact[UserEntryFields.contacts__first_name] || ''} ${contact[UserEntryFields.contacts__last_name] || ''}`.trim() ||
            contact[UserEntryFields.contacts__email] ||
            UNKNOWN_CONTACT,
          email: contact[UserEntryFields.contacts__email] || '',
          data: contact
        }))
      }

      const sourcedBy = dealRecord[DealFields.contact_accounts__sourced_by] || dealRecord.sourced_by_details
      if (sourcedBy && Array.isArray(sourcedBy)) {
        contactsByRole.sourced_by = sourcedBy.map((contact: any) => ({
          value: contact[UserEntryFields.contacts__id] || '',
          label:
            contact[UserEntryFields.prog__display_name] ||
            `${contact[UserEntryFields.contacts__first_name] || ''} ${contact[UserEntryFields.contacts__last_name] || ''}`.trim() ||
            contact[UserEntryFields.contacts__email] ||
            UNKNOWN_CONTACT,
          email: contact[UserEntryFields.contacts__email] || '',
          data: contact
        }))
      }

      const lead = dealRecord[DealFields.contact_deals__lead] || dealRecord.lead_detail
      if (lead && Array.isArray(lead)) {
        contactsByRole.deal_team_lead = lead.map((contact: any) => ({
          value: contact[UserEntryFields.contacts__id] || '',
          label:
            contact[UserEntryFields.prog__display_name] ||
            `${contact[UserEntryFields.contacts__first_name] || ''} ${contact[UserEntryFields.contacts__last_name] || ''}`.trim() ||
            contact[UserEntryFields.contacts__email] ||
            UNKNOWN_CONTACT,
          email: contact[UserEntryFields.contacts__email] || '',
          data: contact
        }))
      }

      setSelectedContacts(contactsByRole)
    }
  }, [dealRecord])

  const [selectedItem, setSelectedItem] = useState<any>(null)
  const [selectedItemType, setSelectedItemType] = useState<'task' | 'interaction' | null>(null) // 'task' or 'interaction'
  const [detailMode, setDetailMode] = useState<
    'view' | 'add-task' | 'add-interaction' | 'edit-task' | 'edit-interaction'
  >('view')

  const handleTaskSelect = useCallback((task: any) => {
    if (task === null) {
      setSelectedItem(null)
      setSelectedItemType(null)
      setDetailMode('view')
    } else {
      setSelectedItem(task)
      setSelectedItemType('task')
      setDetailMode('view')
    }
  }, [])

  const handleAddTask = useCallback(() => {
    setDetailMode('add-task')
    setSelectedItem(null)
    setSelectedItemType('task')
  }, [])

  const handleTaskEditModeChange = useCallback((isInEditMode: boolean) => {
    setIsTaskInEditMode(isInEditMode)
  }, [])

  const handleInteractionSelect = useCallback((interaction: any) => {
    setSelectedItem(interaction)
    setSelectedItemType('interaction')
    setDetailMode('view')
  }, [])

  const handleAddInteraction = useCallback(() => {
    setDetailMode('add-interaction')
    setSelectedItem(null)
    setSelectedItemType('interaction')
  }, [])

  const handleDetailClose = useCallback(() => {
    setSelectedItem(null)
    setSelectedItemType(null)
    setDetailMode('view')
  }, [])

  const handleDetailEdit = useCallback((item: any, itemType: string) => {
    if (itemType === 'task') {
      setDetailMode('edit-task')
    } else if (itemType === 'interaction') {
      setDetailMode('edit-interaction')
    }
  }, [])

  const handleDetailDelete = useCallback(
    async (id: string) => {
      if (!recordId || !id) {
        throw new Error('Record ID is missing')
      }

      if (selectedItemType === 'task') {
        try {
          await dispatch(deleteTaskThunk({ dealId: recordId, taskId: id, recordType: 'deal' })).unwrap()
          setTimeout(() => {
            handleDetailClose()
          }, 100)
        } catch (error) {
          console.error('Error in handleDetailDelete:', error)
          setTimeout(() => {
            handleDetailClose()
          }, 100)
          throw error
        }
        return
      }

      const interactionId = id
      try {
        const currentToken = getAccessToken(store.getState().auth)
        await deleteInteraction(recordId, interactionId, currentToken)
        dispatch(removeInteraction(recordId, interactionId))
        const refreshedInteractions = await fetchInteractionsForDeal(recordId, {}, currentToken)
        dispatch(setInteractions(recordId, refreshedInteractions))
        setTimeout(() => {
          handleDetailClose()
        }, 100)
      } catch (error) {
        console.error('Error in handleDetailDelete:', error)
        setTimeout(() => {
          handleDetailClose()
        }, 100)
        throw error
      }
    },
    [recordId, dispatch, handleDetailClose, selectedItemType]
  )

  const handleDetailDelink = useCallback(
    async (interactionId: string) => {
      if (!recordId || !interactionId) {
        throw new Error('Record ID or Interaction ID is missing')
      }

      try {
        const currentToken = getAccessToken(store.getState().auth)
        await delinkInteraction(recordId, interactionId, 'deal', currentToken)
        dispatch(removeInteraction(recordId, interactionId))
        const refreshedInteractions = await fetchInteractionsForDeal(recordId, {}, currentToken)
        dispatch(setInteractions(recordId, refreshedInteractions))
        setTimeout(() => {
          handleDetailClose()
        }, 100)
      } catch (error) {
        console.error('Error in handleDetailDelink:', error)
        setTimeout(() => {
          handleDetailClose()
        }, 100)
        throw error
      }
    },
    [recordId, dispatch, handleDetailClose]
  )

  const refreshTasksRef = useRef<(() => Promise<void>) | null>(null)

  const handleRefreshTasks = useCallback(async () => {
    if (refreshTasksRef.current) {
      await refreshTasksRef.current()
    }
  }, [])

  // Pre-selection for a task raised from a signal: the deal's lead is the
  // person it would normally fall to. Only a default — the picker stays
  // editable, and an empty list simply leaves it blank.
  const dealLeadAssignees = useMemo(() => {
    const leads = (currentDeal?.[DealFields.contact_deals__lead] ?? []) as any[]
    return (Array.isArray(leads) ? leads : [])
      .filter((lead) => lead?.contacts__id)
      .map((lead) => ({
        value: lead.contacts__id,
        label: lead.prog__display_name || '',
        email: lead.contacts__email || null
      }))
  }, [currentDeal])

  const handleDetailSubmit = async (data: any, mode: string) => {
    if (!recordId || recordId === 'new') {
      return
    }

    const dealContext = dealRecord ?? currentDeal

    try {
      const currentToken = getAccessToken(store.getState().auth)
      if (mode === 'add-task') {
        const taskData = {
          ...data,
          [TaskRecordFields.task_deals__deal_id]: recordId
        }
        const accountIdValue =
          dealContext?.[DealFields.deals__account] ||
          dealContext?.[DealFields.accounts__id] ||
          null
        const accountId =
          typeof accountIdValue === 'object' && accountIdValue?.id
            ? accountIdValue.id
            : accountIdValue
        const createdTask = await createTask(
          recordId,
          taskData,
          currentToken,
          'deal',
          accountId
        )

        if (dealContext) {
          const existingTasks = dealContext[DealFields.prog__tasks] || []
          const updatedTasks = [createdTask, ...existingTasks]
          dispatch(
            updateDealRecord({
              dealId: recordId,
              updates: {
                [DealFields.prog__tasks]: updatedTasks
              }
            })
          )
        }
        await handleRefreshTasks()
      } else if (mode === 'edit-task') {
        const taskData = {
          ...data
        }
        const taskId = selectedItem?.[TaskRecordFields.tasks__id]
        const updatedTask = await updateTask(recordId, taskId, taskData, currentToken)

        if (dealContext) {
          const existingTasks = dealContext[DealFields.prog__tasks] || []
          const updatedTasks = existingTasks.map((t: any) =>
            t[TaskRecordFields.tasks__id] === taskId ? updatedTask : t
          )
          dispatch(
            updateDealRecord({
              dealId: recordId,
              updates: {
                [DealFields.prog__tasks]: updatedTasks
              }
            })
          )
        }
        await handleRefreshTasks()
      } else if (mode === 'add-interaction') {
        const interactionData = {
          ...data,
          [InteractionRecordFields.interaction_deals__deal_id]: recordId
        }
        const accountIdValue =
          dealContext?.[DealFields.deals__account] ||
          dealContext?.[DealFields.accounts__id] ||
          null
        const accountId =
          typeof accountIdValue === 'object' && accountIdValue?.id
            ? accountIdValue.id
            : accountIdValue
        const createdInteraction = await createInteraction(
          recordId,
          interactionData,
          currentToken,
          'deal',
          accountId
        )

        dispatch(addInteraction(recordId, createdInteraction))
      } else if (mode === 'edit-interaction') {
        const interactionData = {
          ...data
        }
        const interactionId = selectedItem?.[InteractionRecordFields.interactions__id]
        const updatedInteraction = await updateInteraction(recordId, interactionId, interactionData, currentToken)

        dispatch(updateInteractionAction(recordId, interactionId, updatedInteraction))
      }

      setDetailMode('view')
    } catch (error) {
      console.error('Failed to save:', error)
      throw error
    }
  }

  const handleTeamMemberClose = () => {
    setSelectedTeamMember(null)
  }

  const handleTeamTableChange = useCallback(
    async (updatedCompanyTeamMembers: any[]) => {
      const currentToken = getAccessToken(store.getState().auth)

      if (!currentToken) {
        if (onCompanyTeamMembersChange) {
          onCompanyTeamMembersChange(updatedCompanyTeamMembers)
        }
        return
      }

      try {
        if (recordId && recordId !== 'new') {
          const dealResponse = await fetchDealById(recordId, currentToken)
          if (dealResponse.data && !dealResponse.error) {
            const fetchedDeal = dealResponse.data
            const recordIdString = recordId.toString()

            dispatch(setActiveRecord({ uuid: recordIdString, record: fetchedDeal }))
            dispatch(registerRecordUsage({ uuid: recordIdString, context: 'fullForm' }))
            dispatch(updateDealRecord({ dealId: recordIdString, updates: fetchedDeal }))
          }
        }
      } catch (error) {
        console.error('Error refreshing deal data after team table change:', error)
        if (onCompanyTeamMembersChange) {
          onCompanyTeamMembersChange(updatedCompanyTeamMembers)
        }
      }
    },
    [dispatch, onCompanyTeamMembersChange, recordId]
  )

  const isDetailPanelOpen = useMemo(() => {
    return selectedItem || detailMode !== 'view'
  }, [selectedItem, detailMode])

  const tasksSectionProps = useMemo(
    () => ({
      dealId: recordId !== 'new' ? recordId : null,
      onTaskSelect: handleTaskSelect,
      selectedTaskId: selectedItemType === 'task' ? selectedItem?.id : null,
      onAddTask: handleAddTask,
      onEditModeChange: handleTaskEditModeChange,
      onRefreshRequest: (refreshFn: () => Promise<void>) => {
        refreshTasksRef.current = refreshFn
        if (onRefreshTasksReady) {
          onRefreshTasksReady(refreshFn)
        }
      }
    }),
    [
      recordId,
      selectedItemType,
      selectedItem?.id,
      handleTaskSelect,
      handleAddTask,
      handleTaskEditModeChange,
      onRefreshTasksReady
    ]
  )

  const refreshInteractionsRef = useRef<(() => Promise<void>) | null>(null)

  const interactionsSectionProps = useMemo(
    () => ({
      dealId: recordId !== 'new' ? recordId : null,
      onInteractionSelect: handleInteractionSelect,
      selectedInteractionId: selectedItemType === 'interaction' ? selectedItem?.id : null,
      onAddInteraction: handleAddInteraction,
      onRefreshRequest: (refreshFn: () => Promise<void>) => {
        refreshInteractionsRef.current = refreshFn
        if (onRefreshInteractionsReady) {
          onRefreshInteractionsReady(refreshFn)
        }
      }
    }),
    [
      recordId,
      selectedItemType,
      selectedItem?.id,
      handleInteractionSelect,
      handleAddInteraction,
      onRefreshInteractionsReady
    ]
  )

  return (
    <div
      data-debug='dashboard-tab-container'
      data-component='DashboardTab'
      data-record-id={recordId}
      data-detail-mode={detailMode}
      data-selected-team-member={selectedTeamMember?.id}
      data-selected-item-type={selectedItemType}
      className='flex-1 flex flex-col h-full'
      style={{ overflowY: 'auto', overflowX: 'hidden' }}
    >
      {/* Deal ORIGINATION follower: the Current State card row leads the page
          at full width, with the open origination signals as their own section
          beneath it — the composition every agent surface shares (deal split,
          owner 2026-08-25; signals unfolded from the footer 2026-09-06).
          Execution content lives on the Execution tab only, never here. */}
      {showAgentPanels && recordId ? (
        <div data-debug='dashboard-tab-deal-states-row' className='mb-3'>
          <DealStatesStrip
            dealId={String(recordId)}
            dimension='origination'
            defaultAssignees={dealLeadAssignees}
            onTaskCreated={handleRefreshTasks}
            onRefreshReady={onRefreshDealStatesReady}
          />
        </div>
      ) : null}

      {showAgentPanels && recordId ? (
        <div data-debug='dashboard-tab-deal-signals-section' className='mb-3'>
          <AgentSignalsSection
            contextTable='deals'
            contextId={String(recordId)}
            openOnly
            maxVisible={3}
            hideWhenEmpty
            collapseKey='deal_dashboard_signals'
            agentTypeKeys={['deal_origination_follower']}
            onViewMore={onNavigateToAgentTab}
            onRefreshReady={onRefreshAgentReady}
            defaultAssignees={dealLeadAssignees}
            onTaskCreated={handleRefreshTasks}
          />
        </div>
      ) : null}

      {/* The row is a column-flex item that GROWS into whatever the tab has
          left below the state/signal strips but never shrinks below its own
          content (flex: 1 0 auto): a sparse left column (no Deal Brief, few
          team rows) still gives the Tasks/Interactions column the full tab
          height, and a long left column lets the tab scroll. The
          .dashboard-tab-row stylesheet rules are not loaded, so the class
          alone gave no floor and the row collapsed to the left column. */}
      <Row
        data-debug='dashboard-tab-main-row'
        gutter={[16, 0]}
        className='dashboard-tab-row dashboard-tab-row--content-height grow shrink-0'
      >
        <Col span={isDetailPanelOpen ? (selectedTeamMember ? 4 : 7) : selectedTeamMember ? 10 : 16} className='h-full'>
          <div data-debug='dashboard-tab-left-column'>
            <Row data-debug='dashboard-tab-left-subrow' gutter={[12, 12]}>
              <Col span={isDetailPanelOpen ? 24 : 10}>
                <div data-debug='dashboard-tab-overview-section' className='space-y-2'>
                  <DealOverviewSection
                    formData={dealRecord}
                    handleFieldChange={handleFieldChange}
                    onPatchSuccess={onPatchSuccess}
                    commitments={commitments}
                    handleCommitmentChange={handleCommitmentChange}
                    showTitle={true}
                    recordId={recordId}
                    isDrawer={false}
                    showCurrency={true}
                    cardProps={{ className: 'mb-1' }}
                  />
                </div>
              </Col>

              <Col span={isDetailPanelOpen ? 24 : 14}>
                <div data-debug='dashboard-tab-team-section' className='space-y-2'>
                  <DealTeam
                    dealId={recordId}
                    recordId={recordId}
                    formData={dealRecord}
                    selectedContacts={selectedContacts}
                    onContactsChange={setSelectedContacts}
                    enableAutoPatch={!!recordId}
                    className='mb-2'
                  />

                  <CompanyTeamTable
                    key={companyTeamMembersKey || 'company-team-empty'}
                    mode='existing'
                    companyId={dealRecord?.[DealFields.deals__account]}
                    initialTeamMembers={companyTeamMembers}
                    onChange={handleTeamTableChange}
                    readOnly={false}
                    title='Company Team'
                  />
                </div>
              </Col>

              {/* Deal Brief closes the page, spanning the overview and team
                  columns (owner layout, 2026-08-24). Self-hiding. */}
              {showAgentPanels && recordId ? (
                <Col span={24}>
                  <div data-debug='dashboard-tab-deal-brief-section'>
                    <DealBriefCard dealId={String(recordId)} hideWhenEmpty />
                  </div>
                </Col>
              ) : null}
            </Row>
          </div>
        </Col>

        <Col span={isDetailPanelOpen ? (selectedTeamMember ? 6 : 7) : selectedTeamMember ? 4 : 8}>
          {/* Out of flow so it never drives the row height: it stretches to
              the left column instead, and Interactions scrolls inside it
              rather than clipping at the fold. */}
          <div className='relative h-full min-h-[400px]'>
            <div
              data-debug='dashboard-tab-center-column'
              className='absolute inset-0 flex flex-col overflow-hidden'
            >
              <div data-debug='dashboard-tab-tasks-section'>
                <TasksSection {...tasksSectionProps} />
              </div>

              <div
                data-debug='dashboard-tab-interactions-section'
                className={`overflow-hidden h-full ${isTaskInEditMode ? 'flex-shrink min-h-0' : 'flex-1 min-h-0'}`}
              >
                <InteractionsSection {...interactionsSectionProps} />
              </div>
            </div>
          </div>
        </Col>

        {isDetailPanelOpen && (
          <Col span={selectedTeamMember ? 8 : 10} className='h-full'>
            <div data-debug='dashboard-tab-detail-column' className='h-full flex flex-col overflow-hidden'>
              <DetailPanel
                selectedItem={selectedItem}
                itemType={selectedItemType || undefined}
                mode={detailMode}
                onClose={handleDetailClose}
                onEdit={handleDetailEdit}
                onSubmit={handleDetailSubmit}
                onDelete={handleDetailDelete}
                onDelink={handleDetailDelink}
                onRefreshInteractions={async () => {
                  if (refreshInteractionsRef.current) {
                    await refreshInteractionsRef.current()
                  }
                }}
                onRefreshTasks={handleRefreshTasks}
              />
            </div>
          </Col>
        )}

        {selectedTeamMember && (
          <Col span={isDetailPanelOpen ? 6 : 6} className='h-full'>
            <div data-debug='dashboard-tab-team-drawer' className='h-full bg-white border-l border-gray-200 shadow-lg'>
              <div
                data-debug='dashboard-tab-team-drawer-header'
                className='flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-br from-blue-400 via-blue-500 to-blue-700'
              >
                <div data-debug='dashboard-tab-team-drawer-title' className='flex-1'>
                  <h3 className='text-lg font-semibold text-white truncate'>{selectedTeamMember.name} Details</h3>
                </div>
                <div data-debug='dashboard-tab-team-drawer-actions' className='flex items-center space-x-2'>
                  <button
                    data-debug='dashboard-tab-team-drawer-close-btn'
                    onClick={handleTeamMemberClose}
                    className='text-white hover:text-white/80 hover:bg-white/10 p-1 rounded'
                    title='Close'
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              </div>

              <div data-debug='dashboard-tab-team-drawer-content' className='flex-1 overflow-y-auto'>
                <div className='p-4'>
                  <TeamMemberDrawer member={selectedTeamMember} onClose={handleTeamMemberClose} />
                </div>
              </div>
            </div>
          </Col>
        )}
      </Row>
    </div>
  )
}
